import { getStoredUser } from '../utils/authUser';
import { SITE_CONFIG } from '../config';

const ID = SITE_CONFIG.ANALYTICS.GA4_MEASUREMENT_ID;
const PREFERENCE_KEY = `treas:analytics:${SITE_CONFIG.SITE_ID}:preference`;
export const ANALYTICS_PREFERENCE_EVENT = 'maricopa-analytics-preference';
let configured = false;
let lastPage = null;

const storage = {
  get() { try { return localStorage.getItem(PREFERENCE_KEY); } catch { return null; } },
  set(value) { try { localStorage.setItem(PREFERENCE_KEY, value); } catch { /* Best effort. */ } }
};

export const getAnalyticsPrivacyState = () => {
  const preference = storage.get();
  const gpc = navigator.globalPrivacyControl === true;
  return { enabled: !gpc && preference !== 'opted_out', gpc, decided: Boolean(preference) };
};

export const isAnalyticsPublicPath = (pathname) => (
  /^\/(?:services|projects|guides|blog)(?:\/[a-z0-9-]+)?\/?$/.test(pathname) ||
  /^\/(?:about-us|contact|reviews|financing|faq|privacy-policy|terms-of-service)?\/?$/.test(pathname)
);

const isProductionHost = () => {
  const host = new URL(SITE_CONFIG.SITE_URL).hostname;
  return window.location.hostname === host || window.location.hostname === `www.${host}`;
};

// Only public content paths and bounded campaign labels can reach GA. Never pass form data.
export const sanitizePageLocation = (value) => {
  const url = new URL(value, SITE_CONFIG.SITE_URL);
  const clean = new URL(isAnalyticsPublicPath(url.pathname) ? url.pathname : '/', SITE_CONFIG.SITE_URL);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_id'].forEach(key => {
    const label = url.searchParams.get(key);
    if (label && /^[a-z0-9_-]{1,80}$/i.test(label)) clean.searchParams.set(key, label);
  });
  return clean.toString();
};

const safeReferrer = () => {
  try { return document.referrer ? new URL(document.referrer).origin : ''; } catch { return ''; }
};

export const initializeAnalytics = () => {
  const allowed = isProductionHost() && isAnalyticsPublicPath(window.location.pathname) && getAnalyticsPrivacyState().enabled;
  window[`ga-disable-${ID}`] = !allowed;
  if (!allowed) { lastPage = null; return false; }
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  if (!configured) {
    window.gtag('js', new Date());
    window.gtag('config', ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: sanitizePageLocation(window.location.href),
      page_referrer: safeReferrer()
    });
    configured = true;
  }
  if (!document.querySelector('script[data-maricopa-analytics]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ID}`;
    script.dataset.maricopaAnalytics = 'true';
    document.head.appendChild(script);
  }
  return true;
};

export const trackPageView = () => {
  try {
    if (!initializeAnalytics()) return;
    const location = sanitizePageLocation(window.location.href);
    if (location === lastPage) return;
    const params = { page_location: location, page_referrer: lastPage || safeReferrer(), page_title: document.title };
    window.gtag('set', params);
    window.gtag('event', 'page_view', { ...params, send_to: ID });
    lastPage = location;
  } catch { /* Analytics must never block navigation. */ }
};

export const trackInquiry = (formId, response) => {
  try {
    if (!response?.ok || response.honeypotBlocked || !['consultation_modal', 'contact_page', 'lead_popup'].includes(formId) || !initializeAnalytics()) return;
    window.gtag('event', 'generate_lead', { form_id: formId, page_location: sanitizePageLocation(window.location.href), send_to: ID });
  } catch { /* A recorded inquiry must still succeed when analytics is unavailable. */ }
};

export const trackContactClick = (kind) => {
  try {
    if (!['phone_click', 'email_click'].includes(kind) || !initializeAnalytics()) return;
    window.gtag('event', kind, { page_location: sanitizePageLocation(window.location.href), send_to: ID });
  } catch { /* Never block a phone or email link. */ }
};


// Shared consented identity contract used by the CRM monitoring endpoints.
const ANALYTICS_BASE_URL = 'https://tophamtechbe.com/api/treas/auth/analytics/identity';
const MEASUREMENT_ID = ID;
const SITE_KEY = String(SITE_CONFIG.SITE_ID).toLowerCase();
const STORAGE_PREFIX = `treas:analytics:${SITE_KEY}`;
const NOTICE_KEY = `${STORAGE_PREFIX}:notice-seen`;
const VISITOR_KEY = `${STORAGE_PREFIX}:visitor-id`;
const LEAD_SUBJECT_KEY = `${STORAGE_PREFIX}:lead-subject-id`;
const PENDING_DELETIONS_KEY = `${STORAGE_PREFIX}:pending-deletions`;
const TOUCH_PREFIX = `${STORAGE_PREFIX}:last-touch:`;
const OPTED_OUT = 'opted_out';
const ENABLED = 'enabled';
const TOUCH_INTERVAL_MS = 24 * 60 * 60 * 1000;
const PREFERENCE_EVENT = ANALYTICS_PREFERENCE_EVENT;
const activeTouchRequests = new Map();
const safeStorage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Analytics must never interfere with the core site experience.
    }
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // Analytics must never interfere with the core site experience.
    }
  }
};

const dispatchPreferenceChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new window.CustomEvent(PREFERENCE_EVENT));
  }
};

const hasGlobalPrivacyControl = () => (
  typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true
);

export const hasSeenAnalyticsNotice = () => safeStorage.get(NOTICE_KEY) === 'true';

export const markAnalyticsNoticeSeen = () => {
  safeStorage.set(NOTICE_KEY, 'true');
};

const createUuid = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const getOrCreateVisitorId = () => {
  if (!getAnalyticsPrivacyState().enabled) return null;

  const existing = safeStorage.get(VISITOR_KEY);
  if (existing) return existing;

  const visitorId = createUuid();
  safeStorage.set(VISITOR_KEY, visitorId);
  return visitorId;
};

const getGaClientId = () => new Promise((resolve) => {
  if (!getAnalyticsPrivacyState().enabled || typeof window.gtag !== 'function') {
    resolve(null);
    return;
  }

  let finished = false;
  const finish = (value) => {
    if (finished) return;
    finished = true;
    resolve(typeof value === 'string' && value.length <= 255 ? value : null);
  };

  const timeoutId = window.setTimeout(() => finish(null), 800);
  window.gtag('get', MEASUREMENT_ID, 'client_id', (clientId) => {
    window.clearTimeout(timeoutId);
    finish(clientId);
  });
});

export const getAnalyticsIdentityForAuth = async () => {
  if (!isProductionHost() || !getAnalyticsPrivacyState().enabled) return null;

  try {
    initializeAnalytics();
    const visitorId = getOrCreateVisitorId();
    if (!visitorId) return null;

    const gaClientId = await getGaClientId();
    if (!getAnalyticsPrivacyState().enabled) return null;
    return {
      visitorId,
      ...(gaClientId ? { gaClientId } : {}),
      analyticsConsent: true
    };
  } catch (error) {
    return null;
  }
};

const configureAnalyticsUser = (analyticsUserId) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('config', MEASUREMENT_ID, {
    user_id: analyticsUserId || null,
    send_page_view: false
  });
};

export const applyLeadAnalyticsUser = (analyticsUserId) => {
  if (!getAnalyticsPrivacyState().enabled || typeof analyticsUserId !== 'string' || !analyticsUserId.trim()) {
    return false;
  }
  initializeAnalytics();
  safeStorage.set(LEAD_SUBJECT_KEY, analyticsUserId.trim());
  configureAnalyticsUser(analyticsUserId.trim());
  return true;
};

export const clearAnalyticsUser = () => {
  if (typeof window !== 'undefined' && configured) configureAnalyticsUser(null);
};

const getTouchKey = (user, visitorId) => (
  `${TOUCH_PREFIX}${user?.analyticsUserId || user?.id || 'unknown'}:${visitorId}`
);

const getAuthorizationHeaders = (user) => {
  if (!user?.token) return null;
  return {
    Authorization: `${user.type || 'Bearer'} ${user.token}`,
    'Content-Type': 'application/json'
  };
};

export const touchAnalyticsIdentity = async (user, { force = false } = {}) => {
  if (!isProductionHost() || !getAnalyticsPrivacyState().enabled) return false;

  const headers = getAuthorizationHeaders(user);
  const visitorId = getOrCreateVisitorId();
  if (!headers || !visitorId) return false;

  const touchKey = getTouchKey(user, visitorId);
  const lastTouch = Number(safeStorage.get(touchKey));
  if (!force && Number.isFinite(lastTouch) && Date.now() - lastTouch < TOUCH_INTERVAL_MS) {
    return true;
  }

  if (activeTouchRequests.has(touchKey)) return activeTouchRequests.get(touchKey);

  const request = (async () => {
    try {
      const gaClientId = await getGaClientId();
      if (!getAnalyticsPrivacyState().enabled) return false;
      const response = await fetch(`${ANALYTICS_BASE_URL}/touch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          visitorId,
          ...(gaClientId ? { gaClientId } : {}),
          analyticsConsent: true
        })
      });

      if (!response.ok) return false;
      safeStorage.set(touchKey, String(Date.now()));
      return true;
    } catch (error) {
      return false;
    } finally {
      activeTouchRequests.delete(touchKey);
    }
  })();

  activeTouchRequests.set(touchKey, request);
  return request;
};

const readPendingDeletions = () => {
  try {
    const parsed = JSON.parse(safeStorage.get(PENDING_DELETIONS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writePendingDeletions = (pending) => {
  if (pending.length) safeStorage.set(PENDING_DELETIONS_KEY, JSON.stringify(pending));
  else safeStorage.remove(PENDING_DELETIONS_KEY);
};

const queuePendingDeletion = (user, visitorId) => {
  if (!visitorId) return;
  const pending = readPendingDeletions();
  const userKey = user?.token ? String(user.analyticsUserId || user.id || '') : '';
  if (!pending.some((item) => item.visitorId === visitorId && item.userKey === userKey)) {
    pending.push({ visitorId, userKey, queuedAt: new Date().toISOString() });
    writePendingDeletions(pending);
  }
};

const deleteAnonymousIdentityMapping = async (visitorId) => {
  if (!visitorId) return false;
  try {
    const response = await fetch(`${ANALYTICS_BASE_URL.replace(/\/identity$/, '')}/lead-identity`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId: SITE_CONFIG.SITE_ID, visitorId })
    });
    return response.status === 204;
  } catch (error) {
    return false;
  }
};

const deleteIdentityMapping = async (user, visitorId) => {
  const headers = getAuthorizationHeaders(user);
  if (!headers || !visitorId) return false;

  try {
    const response = await fetch(ANALYTICS_BASE_URL, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ visitorId })
    });
    return response.status === 204;
  } catch (error) {
    return false;
  }
};

export const retryPendingAnalyticsDeletions = async (user) => {
  const userKey = String(user?.analyticsUserId || user?.id || '');
  const pending = readPendingDeletions();
  if (!pending.length) return true;

  const remaining = [];
  for (const item of pending) {
    if (item.userKey && (!user?.token || item.userKey !== userKey)) {
      remaining.push(item);
      continue;
    }
    // Keep failed deletions for a later retry. Browser-only withdrawals use
    // the site-scoped visitor UUID as deletion proof and expose no data.
    // eslint-disable-next-line no-await-in-loop
    const deleted = item.userKey
      ? await deleteIdentityMapping(user, item.visitorId)
      : await deleteAnonymousIdentityMapping(item.visitorId);
    if (!deleted) remaining.push(item);
  }
  writePendingDeletions(remaining);
  return remaining.length === 0;
};

const removeTouchMarkers = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(TOUCH_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    // Analytics must never interfere with the core site experience.
  }
};

const clearGaCookies = () => {
  if (typeof document === 'undefined') return;
  const gaCookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));

  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  const domains = ['', hostname, `.${hostname}`];
  if (domainParts.length > 2) domains.push(`.${domainParts.slice(-2).join('.')}`);

  gaCookieNames.forEach((name) => {
    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
    });
  });
};

export const disableAnalytics = async (user) => {
  const visitorId = safeStorage.get(VISITOR_KEY);
  window[`ga-disable-${MEASUREMENT_ID}`] = true;
  lastPage = null;
  clearAnalyticsUser();
  safeStorage.set(PREFERENCE_KEY, OPTED_OUT);
  markAnalyticsNoticeSeen();
  window[`ga-disable-${MEASUREMENT_ID}`] = true;

  if (visitorId) queuePendingDeletion(user, visitorId);
  safeStorage.remove(VISITOR_KEY);
  safeStorage.remove(LEAD_SUBJECT_KEY);
  removeTouchMarkers();
  clearGaCookies();
  dispatchPreferenceChange();

  await retryPendingAnalyticsDeletions(user);
};

export const enableAnalytics = async (user) => {
  if (hasGlobalPrivacyControl()) return false;
  safeStorage.set(PREFERENCE_KEY, ENABLED);
  markAnalyticsNoticeSeen();
  window[`ga-disable-${MEASUREMENT_ID}`] = false;
  initializeAnalytics();
  if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
  lastPage = null;
  dispatchPreferenceChange();
  retryPendingAnalyticsDeletions(user);
  if (user?.token) {
    touchAnalyticsIdentity(user, { force: true });
  }
  return true;
};

export const completeAnalyticsAuthentication = (user, eventName) => {
  try {
    if (!getAnalyticsPrivacyState().enabled) return;
    initializeAnalytics();
    safeStorage.remove(LEAD_SUBJECT_KEY);
    if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
    if (eventName && initializeAnalytics() && typeof window.gtag === 'function') {
      window.gtag('event', eventName, { method: 'website' });
    }
    touchAnalyticsIdentity(user);
    retryPendingAnalyticsDeletions(user);
  } catch { /* Analytics never blocks authentication. */ }
};

export const syncStoredAnalyticsUser = (user) => {
  try {
    if (!isProductionHost()) return;
    if (!getAnalyticsPrivacyState().enabled) {
      retryPendingAnalyticsDeletions(user);
      return;
    }
    initializeAnalytics();
    if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
    else {
      const leadSubjectId = safeStorage.get(LEAD_SUBJECT_KEY);
      if (leadSubjectId) configureAnalyticsUser(leadSubjectId);
    }
    touchAnalyticsIdentity(user);
    retryPendingAnalyticsDeletions(user);
  } catch { /* Analytics never blocks navigation. */ }
};


export const setAnalyticsPreference = enabled => enabled ? enableAnalytics(getStoredUser()) : disableAnalytics(getStoredUser());
